// Pure, locale-aware percentage formatter (contract 04 §3.2, §5). Replaces the legacy
// `appendPercentageSymbol` / `formatAsPercentage` (`format.js:355–363`), which string-appended a
// literal `'%'` after a browser-locale number. Here the `%` and its placement come from Intl's
// `style: 'percent'`, so it is locale-correct.

import {
  SENTINEL,
  commonNumberOptions,
  getNumberFormat,
  guard,
  resolveFraction,
} from './createFormatter'
import type { FormatPercentOptions } from './types'

/** Percentages default to a fixed 2 fraction digits (contract §16: `0.1234 → "12.34%"`). */
const PERCENT_FRACTION = { min: 2, max: 2 }

/**
 * Format a percentage. `scale: 'ratio'` (default) treats the value as a 0–1 fraction
 * (`0.1234 → "12.34%"`); `scale: 'percent'` treats it as an already-scaled percentage
 * (`12.34 → "12.34%"`). Intl's percent style multiplies a ratio by 100, so a percent-scaled input
 * is divided back to a ratio first.
 */
export function formatPercent(
  value: number | null | undefined,
  locale: string,
  options: FormatPercentOptions = {},
): string {
  if (guard(value)) return SENTINEL

  const ratio = options.scale === 'percent' ? value / 100 : value

  return getNumberFormat(locale, {
    style: 'percent',
    ...commonNumberOptions(options),
    ...resolveFraction(options, PERCENT_FRACTION),
  }).format(ratio)
}
